import { getSessionId, initSessionContext } from './session';
import { enqueueDevLogLine, formatDevLogLine, initDevFileSink } from './devFileSink';
import { initConsoleCapture, writeToNativeConsole } from './consoleCapture';
import { appendLogBufferEntry } from './logBufferStorage';
import {
  applyTracingHeaders,
  generateTraceId,
  HEADER_SESSION_ID,
  HEADER_TRACE_ID,
} from './trace';

const LEVEL_RANK = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function resolveMinLevel() {
  const configured = String(import.meta.env.VITE_APP_LOG_LEVEL || '').toLowerCase();
  if (configured in LEVEL_RANK) {
    return configured;
  }
  return import.meta.env.DEV ? 'debug' : 'warn';
}

let minLevel = resolveMinLevel();

function shouldLog(level) {
  return LEVEL_RANK[level] >= LEVEL_RANK[minLevel];
}

function formatPrefix(level, module, traceId) {
  const sessionId = getSessionId();
  const parts = [`[${level.toUpperCase()}]`];
  if (module) {
    parts.push(`[${module}]`);
  }
  if (sessionId) {
    parts.push(`[sess:${sessionId.slice(0, 13)}]`);
  }
  if (traceId) {
    parts.push(`[trace:${String(traceId).slice(0, 13)}]`);
  }
  return parts.join('');
}

function emit(level, module, message, data, traceId) {
  if (!shouldLog(level)) {
    return;
  }

  const prefix = formatPrefix(level, module, traceId);
  const writerLevel = level === 'error'
    ? 'error'
    : level === 'warn'
      ? 'warn'
      : level === 'debug'
        ? 'debug'
        : 'log';

  if (data !== undefined) {
    writeToNativeConsole(writerLevel, prefix, message, data);
  } else {
    writeToNativeConsole(writerLevel, prefix, message);
  }

  if (import.meta.env.DEV) {
    enqueueDevLogLine(formatDevLogLine(level, module, message, data, traceId));
  }

  appendLogBufferEntry({
    level,
    module,
    message,
    data,
    traceId,
    line: formatDevLogLine(level, module, message, data, traceId),
    timestamp: Date.now(),
  });
}

export const logger = {
  debug(module, message, data, traceId) {
    emit('debug', module, message, data, traceId);
  },
  info(module, message, data, traceId) {
    emit('info', module, message, data, traceId);
  },
  warn(module, message, data, traceId) {
    emit('warn', module, message, data, traceId);
  },
  error(module, message, data, traceId) {
    emit('error', module, message, data, traceId);
  },
  getMinLevel() {
    return minLevel;
  },
  setMinLevel(level) {
    if (level in LEVEL_RANK) {
      minLevel = level;
    }
  },
};

export default logger;
export {
  initSessionContext,
  initDevFileSink,
  initConsoleCapture,
  getSessionId,
  applyTracingHeaders,
  generateTraceId,
  HEADER_SESSION_ID,
  HEADER_TRACE_ID,
};

/**
 * 为业务模块创建带固定 module 名的 logger，签名贴近 console：
 * log.debug(message, data?)
 */
export function createModuleLogger(module) {
  const wrap = (level) => (message, ...rest) => {
    const data = rest.length === 0 ? undefined : rest.length === 1 ? rest[0] : rest;
    logger[level](module, message, data);
  };
  return {
    debug: wrap('debug'),
    info: wrap('info'),
    warn: wrap('warn'),
    error: wrap('error'),
  };
}
